/**
 * Canonical HelperKind enum — single source of truth for the eight
 * helper-chat agent kinds that ship with ArgusAI.
 *
 * Before this file existed (B-0 Foundation Wiring, 2026-05-24) the
 * `HelperKind` union was declared in FIVE places that quietly drifted:
 *
 *   1. packages/api/src/routes/v1/studio-helper-chat.ts (Zod enum + STUDIO_HELPER_AGENT_KINDS)
 *   2. packages/ai-core/src/helper-mutation-tools.ts    (TS union — missed `ask_argus`)
 *   3. packages/api/src/services/helper-chat-memory.service.ts (TS union)
 *   4. packages/web/src/lib/use-studio-helper.ts        (TS union — missed `ask_argus`)
 *   5. packages/api/src/routes/v1/studio-helper-chat.ts (KIND_SNAPSHOT_STRIP local alias)
 *
 * The drift in (2) caused `getMutationToolsFor('ask_argus', scope)` to
 * fall through the switch and return `undefined` — which then got passed
 * to `streamText({ tools: undefined })` so Ask Argus shipped without
 * mutation tools AND its system prompt's `ext.consult.coder` /
 * `ext.consult.vision` instructions referenced tools the model could not
 * see. The CI gate at `scripts/ci/helper-kind-drift-check.mjs` now
 * fails the build if any file re-declares the union locally instead of
 * importing from this module.
 *
 * Ordering rule: list is sorted alphabetically and frozen. Add new kinds
 * in alphabetical order, and update the test in
 * `packages/api/src/routes/v1/__tests__/helper-kind-canonical.test.ts`
 * which asserts the ordering + cardinality. Do not change the order on
 * existing entries — downstream tests rely on it for deterministic
 * snapshotting.
 */

import { z } from 'zod';

/**
 * The eight canonical helper kinds.
 *
 * - `architect`              — Argus Architect: agent-builder helper
 * - `argus_live_architect`   — Argus Live publish-time safety reviewer (canonical-9)
 * - `argus_live_author`      — Argus Live dashboard authoring helper (AL-B4)
 * - `ask_argus`              — Ask Argus chat brain (the only kind with cross-chat memory)
 * - `function_helper`        — Custom-function authoring helper
 * - `prompt_helper`          — Prompt-template authoring helper
 * - `skill_helper`           — Skill authoring helper
 * - `tool_helper`            — Tool authoring helper
 *
 * Note: `specialist` (system.argus_code et al.) is intentionally NOT in
 * this list — specialists are invoked from within Ask Argus chat via
 * tool calls, not directly through the helper-chat route. They live in
 * `system_agents.kind = 'specialist'` and are filtered out of the
 * helper-chat agent list by `STUDIO_HELPER_AGENT_KINDS`.
 */
export const HELPER_KINDS = [
  'architect',
  'argus_live_architect',
  'argus_live_author',
  'ask_argus',
  'function_helper',
  'prompt_helper',
  'skill_helper',
  'tool_helper',
] as const;

/** Zod enum — use this in request body schemas. */
export const HelperKindSchema = z.enum(HELPER_KINDS);

/** Discriminated string-literal union derived from `HELPER_KINDS`. */
export type HelperKind = (typeof HELPER_KINDS)[number];

/**
 * Type-guard: narrows `unknown` to `HelperKind`. Convenience for places
 * that read kind off raw row data or external input.
 */
export function isHelperKind(value: unknown): value is HelperKind {
  return typeof value === 'string' && (HELPER_KINDS as readonly string[]).includes(value);
}

/**
 * Kinds that are scoped to the canvas-builder helpers (the original
 * five). Used by snapshot evictions and discovery prompts that only
 * apply to canvas authoring.
 */
export const CANVAS_BUILDER_KINDS = [
  'architect',
  'function_helper',
  'prompt_helper',
  'skill_helper',
  'tool_helper',
] as const satisfies readonly HelperKind[];

/**
 * Kinds that NEVER receive cross-chat memory, even when the tenant
 * feature toggle is on. Per founder direction, only `ask_argus` carries
 * conversation context across sessions. All canvas builders + the
 * Argus Live pair are intra-chat-only.
 */
export const HELPER_KINDS_NO_CROSS_CHAT = [
  'architect',
  'argus_live_architect',
  'argus_live_author',
  'function_helper',
  'prompt_helper',
  'skill_helper',
  'tool_helper',
] as const satisfies readonly HelperKind[];

/**
 * `system_agents.kind` SUPERSET — the set of values the seed loader
 * accepts on incoming agents.json rows AND the set of values that can
 * legitimately appear in the `system_agents` table's `kind` column.
 *
 * Equals `HELPER_KINDS` + the `specialist` kind. Specialists like
 * `system.argus_code` participate in Ask Argus chat via `@mention`
 * delegation but are NOT addressable through the helper-chat route
 * directly (they don't appear in `STUDIO_HELPER_AGENT_KINDS`).
 *
 * Use this when validating raw `system_agents.kind` values; use
 * `HELPER_KINDS` when you need only the helper-chat-routable subset.
 */
export const SYSTEM_AGENT_KINDS = [
  ...HELPER_KINDS,
  'specialist',
] as const;

import { z as _z } from 'zod';
/** Zod enum for `system_agents.kind` (superset of helper kinds). */
export const SystemAgentKindSchema = _z.enum(SYSTEM_AGENT_KINDS);
/** TS union derived from `SYSTEM_AGENT_KINDS`. */
export type SystemAgentKind = (typeof SYSTEM_AGENT_KINDS)[number];
